import { useState, useEffect, useMemo } from "react";
import { Row, Col, Input, Space, Tooltip, Tag, Select } from "antd";
import { SelectOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import DataTable from "../DataTable";
import AuditModal from "../ModalForm/Member/AuditModal";
import { isDocumentAuditAction } from "../../utils/documentAudit";

const { Search } = Input;

interface IAuditProps {
  audits: any;
}

type AuditFilter = "all" | "document";

const AuditTable: React.FC<IAuditProps> = ({ audits }) => {
  const [all, setAll] = useState<any[]>([]);
  const [state, setState] = useState({
    loading: true,
    pagination: {
      pageSize: 15,
      position: ["bottomRight"] as ("bottomRight")[],
      total: 0,
      current: 1,
      hideOnSinglePage: true,
    },
    searchItem: "",
    filter: "all" as AuditFilter,
  });
  const [show, setShow] = useState(false);
  const [content, setContent] = useState<string>("");

  const showModal = (record: any) => {
    setShow(!show);
    setContent(record);
  };

  const getDescriptionSummary = (newValue: string) => {
    try {
      if (!newValue) return "No details available";

      const jsonData =
        typeof newValue === "string" ? JSON.parse(newValue) : newValue;

      if (typeof jsonData === "object" && jsonData !== null) {
        const keys = Object.keys(jsonData);
        if (keys.length === 0) return "Empty change record";

        if (jsonData.field || jsonData.changedField) {
          return `Changed: ${jsonData.field || jsonData.changedField}`;
        }

        if (jsonData.Name || jsonData.documentType || jsonData.DocumentType) {
          const name = jsonData.Name || jsonData.name || "document";
          const type =
            jsonData.DocumentType || jsonData.documentType || jsonData.Type;
          return type ? `${name} (${type})` : String(name);
        }

        return `Modified: ${keys.slice(0, 3).join(", ")}${
          keys.length > 3 ? "..." : ""
        }`;
      }

      return (
        String(newValue).substring(0, 50) +
        (String(newValue).length > 50 ? "..." : "")
      );
    } catch {
      return (
        String(newValue).substring(0, 50) +
        (String(newValue).length > 50 ? "..." : "")
      );
    }
  };

  const getActionColor = (action: string) => {
    const actionLower = action?.toLowerCase() || "";

    if (isDocumentAuditAction(action)) {
      if (actionLower.includes("delete")) return "red";
      if (actionLower.includes("sent") || actionLower.includes("sms"))
        return "cyan";
      if (actionLower.includes("generat")) return "geekblue";
      if (actionLower.includes("upload")) return "green";
      return "cyan";
    }
    if (actionLower.includes("create")) return "green";
    if (actionLower.includes("delete")) return "red";
    if (actionLower.includes("update")) return "blue";
    if (actionLower.includes("tfs_check")) return "orange";
    if (actionLower.includes("premium_applied")) return "purple";
    if (actionLower.includes("policy_lapse")) return "red";
    if (actionLower.includes("continuation")) return "green";
    return "default";
  };

  const filteredAudits = useMemo(() => {
    const list = Array.isArray(audits) ? audits : [];
    const search = state.searchItem.trim().toLowerCase();

    return list
      .filter((audit: any) => {
        if (state.filter === "document" && !isDocumentAuditAction(audit?.Action)) {
          return false;
        }
        if (!search) return true;
        const haystack = [
          audit?.Action,
          audit?.User?.Name,
          audit?.User?.Role,
          audit?.User?.UserRole,
          typeof audit?.NewValue === "string"
            ? audit.NewValue
            : JSON.stringify(audit?.NewValue ?? ""),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(search);
      })
      .sort(
        (a: any, b: any) => dayjs(b.Date).unix() - dayjs(a.Date).unix()
      );
  }, [audits, state.searchItem, state.filter]);

  useEffect(() => {
    setAll(filteredAudits);
    setState((prev) => ({
      ...prev,
      loading: false,
      pagination: {
        ...prev.pagination,
        total: filteredAudits.length,
        current: 1,
      },
    }));
  }, [filteredAudits]);

  const handlePageChange = (pagination: any) => {
    const currentPage = pagination?.current ?? 1;
    setState((prevState) => ({
      ...prevState,
      pagination: {
        ...prevState.pagination,
        current: currentPage,
      },
    }));
  };

  const hasAnyRole = useMemo(
    () =>
      (Array.isArray(audits) ? audits : []).some(
        (a: any) => a?.User?.Role || a?.User?.UserRole
      ),
    [audits]
  );

  const columns = [
    {
      title: "Date",
      dataIndex: "Date",
      render: (date: Date) => dayjs(date).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "User",
      dataIndex: ["User", "Name"],
    },
    ...(hasAnyRole
      ? [
          {
            title: "Role",
            render: (_: any, record: any) =>
              record?.User?.Role || record?.User?.UserRole || "—",
          },
        ]
      : []),
    {
      title: "Action",
      dataIndex: "Action",
      render: (action: string) => (
        <Tag color={getActionColor(action)}>{action}</Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "NewValue",
      render: (newValue: string) => getDescriptionSummary(newValue),
    },
    {
      title: "Details",
      render: (_: any, record: any) => (
        <Space size="middle">
          <Tooltip title="View Changes">
            <SelectOutlined
              onClick={() => showModal(record.NewValue)}
              style={{ color: "green" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={10}>
          <Search
            size="large"
            placeholder="Search action, user, or details"
            value={state.searchItem}
            onChange={(e) =>
              setState((prev) => ({ ...prev, searchItem: e.target.value }))
            }
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            size="large"
            style={{ width: "100%" }}
            value={state.filter}
            onChange={(filter: AuditFilter) =>
              setState((prev) => ({ ...prev, filter }))
            }
            options={[
              { value: "all", label: "All actions" },
              { value: "document", label: "Document events only" },
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <DataTable
            persistKey="audit"
            dataSource={all}
            columns={columns}
            loading={state.loading}
            pagination={state.pagination}
            onChange={handlePageChange}
          />
        </Col>

        <AuditModal show={show} showModal={showModal} content={content} />
      </Row>
    </>
  );
};

export default AuditTable;
